import { useEffect, useState } from "react";

import { Spinner } from "@blueprintjs/core";

import { Box, Flex } from "@/components/layout/flexbox";

interface VideoEmbedProps {
  url: string;
  altComponent?: React.ReactNode;
}

export function VideoEmbed({ url, altComponent }: VideoEmbedProps) {
  const [embedUrl, setEmbedUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const detectPlatform = () => {
      setIsLoading(true);
      try {
        const parsedUrl = new URL(url);

        if (
          parsedUrl.hostname.includes("youtube.com") ||
          parsedUrl.hostname.includes("youtu.be")
        ) {
          const videoId = parsedUrl.hostname.includes("youtu.be")
            ? parsedUrl.pathname.slice(1)
            : new URLSearchParams(parsedUrl.search).get("v");
          setEmbedUrl(`https://www.youtube.com/embed/${videoId}`);
          setError(null);
        } else if (parsedUrl.hostname.includes("vimeo.com")) {
          const videoId = parsedUrl.pathname.split("/").pop();
          setEmbedUrl(`https://player.vimeo.com/video/${videoId}`);
          setError(null);
        } else if (parsedUrl.hostname.includes("loom.com")) {
          const videoId = parsedUrl.pathname.split("/").pop();
          setEmbedUrl(`https://www.loom.com/embed/${videoId}`);
          setError(null);
        } else if (
          parsedUrl.hostname.includes("wistia.com") ||
          parsedUrl.hostname.includes("wi.st")
        ) {
          const videoId = parsedUrl.pathname.split("/").pop();
          setEmbedUrl(`https://fast.wistia.net/embed/iframe/${videoId}`);
          setError(null);
        } else {
          setError("Unsupported video platform");
        }
      } catch (error) {
        console.error(error);
        setError("Invalid URL");
      } finally {
        setIsLoading(false);
      }
    };

    detectPlatform();
  }, [url]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex justifyContent="center" alignItems="center" height="200px">
          <Spinner size={50} />
        </Flex>
      );
    }

    if (altComponent && (error || !embedUrl)) {
      return altComponent;
    }

    if (error) {
      return (
        <Box color={"#c23030"} fontSize={14} mt={10}>
          {error}
        </Box>
      );
    }

    if (!embedUrl) {
      return (
        <Box color={"#c23030"} fontSize={14} mt={10}>
          No valid video URL detected.
        </Box>
      );
    }

    return (
      <Box>
        <Box
          style={{
            position: "relative",
            paddingBottom: "56.25%", // 16:9 aspect ratio
            height: 0,
            overflow: "hidden",
          }}
        >
          <iframe
            src={embedUrl}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: 0,
            }}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        </Box>
      </Box>
    );
  };

  return renderContent();
}
